import React from "react";
import { useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import theme from "theme";
import BoostContainer from "pages/Boost/components/BoostContainer";
import { Card, Divider, Stack } from "@mui/material";
import PhoneLogo from "images/phone_app_logo.png";
import GradientButton from "@tomocredit/common-fe/components/common/GradientButton";
import RightArrowIcon from "images/arrow-right-black.svg";
import Image1 from "images/how_it_work_img1.png";
import Image2 from "images/how_it_work_img2.png";
import Image3 from "images/how_it_work_img3.png";
import Image4 from "images/how_it_work_img4.png";
import Image5 from "images/how_it_work_img5.png";
import Image6 from "images/how_it_work_img6.png";
import Image7 from "images/how_it_work_img7.png";
import Image8 from "images/how_it_work_img8.png";
import Image9 from "images/how_it_work_img9.png";
import Image10 from "images/how_it_work_img10.png";

const BOOST_PRICING_URL = "/boost-pricing";

const CardContainer = ({ title = "", paragraph = "", children }) => {
  return (
    <Stack
      sx={(muiTheme) => ({
        padding: "2.5rem",
        borderRadius: "10px",
        background: "rgba(255, 255, 255, 0.08)",
        placeContent: "center",
        [muiTheme.breakpoints.down("md")]: {
          padding: "1rem",
        },
      })}
    >
      <div className="grid place-items-center w-full">
        <div className="flex flex-col w-full md:w-1/2">
          <span className="text-2xl font-bold text-center">
            {title}<span className="text-3xl font-bold text-boost-primary-400">.</span>
          </span>
          <p className="text-lg text-center">{paragraph}</p>
        </div>
      </div>
      {children}
    </Stack>
  );
};

export const InfoCard = ({ titleOne = "", paragraphOne = "", titleTwo = "", paragraphTwo = "", heroImage = null, imageClass = "", ctaText = "", reversed = false }) => {
  const history = useHistory();

  return (
    <div className={`flex flex-col-reverse ${reversed ? "md:flex-row-reverse" : "md:flex-row"} justify-between max-w-page py-10`}>
      <Card
        sx={(muiTheme) => ({
          padding: "2.5rem",
          borderRadius: "10px",
          background: theme.colors["neutral-700"],
          color: 'white',
          flexGrow: 1,
          margin: "auto",
          maxWidth: "41rem",
          [muiTheme.breakpoints.down("md")]: {
            padding: "2rem",
          },
        })}
      >
        {titleOne &&
          <p className="text-3xl font-bold">{titleOne}</p>
        }
        {paragraphOne &&
          <p className="mt-4 text-lg">{paragraphOne}</p>
        }
        {titleTwo &&
          <>
            <div className="py-6">
              <Divider sx={{ background: "#8E8E8E" }} />
            </div>
            <p className="text-3xl font-bold">{titleTwo}</p>
          </>
        }
        {paragraphTwo &&
          <p className="mt-4 text-lg">{paragraphTwo}</p>
        }
        {ctaText &&
          <GradientButton color="black" marginTop="2rem" clickMethod={() => history.push(BOOST_PRICING_URL)}>
            <span className="flex flex-row">{ctaText} <img src={RightArrowIcon} /></span>
          </GradientButton>
        }
      </Card>
      <div className="p-6 mb-4 md:mb-0 grid place-items-center flex-grow">
        <img src={heroImage} className={imageClass} />
      </div>
    </div>
  );
};

const HowItWorks = () => {
  return (
    <BoostContainer relative>
      <Helmet>
        <title>How it works</title>
      </Helmet>
      <div className="max-w-page h-full mb-20">
        <div id="hero-banner" className="w-full flex flex-col-reverse md:flex-row items-center justify-between p-10 mb-16">
          <div className="w-full md:w-1/2 justify-center">
            <h1 className="howTitle">Take Control of Your Credit and Cash Flow with Tomo.</h1>
            <p className="mt-2 font-semibold text-base">Paying your bills on time is a major factor in building your credit score, which directly impacts your ability to get an apartment, car, home, or business loan. Tomo helps you track both bills and credit, so you can stay on top of your finances and unlock financial freedom.</p>
          </div>
          <img src={PhoneLogo} className="mb-8 md:mb-0" style={{ width: "363px", height: "455px" }} />
        </div>
        <Stack spacing={6}>
          {/* Bills */}
          <CardContainer
            title="Monitor Your Bills Effortlessly"
            paragraph="Join over 4 million customers who have linked 15 million+ bank accounts. We monitor your bills, so you don’t have to."
          >
            <InfoCard 
              titleOne="Track All Your Bills in One Place"
              paragraphOne="Have too many bills to track? Tomo can automatically track your bills from multiple bank accounts - we are connected to more than 10,000 financial institutions and banks! Add and manage bank accounts easily and stay on top of your cash flow."
              heroImage={Image1}
              imageClass="h-auto md:h-48"
            />
            <InfoCard 
              titleOne="Real-Time Updates"
              paragraphOne="View bills and transactions instantly. We auto-categorize your bills for you."
              heroImage={Image2}
              imageClass="h-auto md:h-80"
              reversed
            />
            <InfoCard 
              titleOne="Take Action"
              paragraphOne="Manage subscriptions, budget better, and receive alerts for high spending. Tomo helps you stay on top of what’s due, pay bills on time, and avoid late fees and overdrafts. Let’s start saving money together!"
              heroImage={Image3}
              imageClass="h-auto md:h-80"
              ctaText="💡 Get Started Today"
            />
          </CardContainer>
          {/* Credit */}
          <CardContainer
            title="Build Better Credit"
            paragraph="Join over 4 million customers in improving your credit health.  On average, our members have seen a +105 credit score increase during their membership with us!"
          >
            <InfoCard 
              titleOne="Enroll in Credit Monitoring"
              paragraphOne="Get real-time alerts and keep track of your credit score to stay ahead of any changes."
              heroImage={Image4}
              imageClass="h-auto md:h-96"
            />
            <InfoCard 
              titleOne="Free Credit Report"
              paragraphOne="Get free access to your credit report whenever you want, a $50 value! Gain full insight into your credit profile—credit utilization, credit age, inquiries, and open accounts—to see exactly what's impacting your score."
              heroImage={Image5}
              imageClass="h-auto md:h-80"
              reversed
            />
            <InfoCard 
              titleOne="Fix Credit Errors"
              paragraphOne="Did you know 1 out of 5 people have errors on their credit report from all 3 bureaus: Experian, Equifax and Transunion?  Take action by identifying and correcting any inaccuracies on your credit report that may be hurting your score."
              heroImage={Image6}
              imageClass="h-auto md:h-[480px]"
              ctaText="💡 Get Started Today"
            />
          </CardContainer>
          {/* Dispute */}
          <CardContainer
            title="Fix Credit on Autopilot with TomoAI"
            paragraph="The credit reporting system is broken—up to 79% of credit reports contain errors, and 25% of those errors can affect loan approvals. TomoBoost AI makes credit repair easy."
          >
            <InfoCard 
              titleOne="Automated Disputes"
              paragraphOne="Our AI scans your credit report for inaccuracies and negative remarks.  You can file a dispute conveniently with all 3 credit bureaus with the help of AI."
              heroImage={Image7}
              imageClass="h-auto md:h-96"
            />
            <InfoCard 
              titleOne="Simple Setup"
              paragraphOne="Answer a few quick questions, and let us handle the rest."
              titleTwo="Boost Your Approval Chances"
              paragraphTwo="Improve your credit and increase your chances of getting approved for a car loan, mortgage, or business financing—effortlessly."
              heroImage={Image8}
              imageClass="h-auto md:h-96"
              reversed
            />
          </CardContainer>
          {/* Counseling */}
          <CardContainer
            title="Personalized Credit Counseling"
            paragraph="We know that managing finances and building credit can be challenging. Let us guide you through the process with personalized support."
          >
            <InfoCard 
              titleOne="1:1 Credit Counseling Calls"
              paragraphOne="Schedule a one-on-one session for personalized guidance and practical steps to improve your credit. Learn how to manage your money, build better financial habits, and avoid common mistakes."
              heroImage={Image9}
              imageClass="h-auto md:h-48"
            />
            <InfoCard 
              titleOne="Instant AI Assistance"
              paragraphOne="Get immediate answers to your credit questions with our AI-powered assistant—available 24/7."
              titleTwo="Credit & Financial Education"
              paragraphTwo="Gain a deeper understanding of how credit works, and learn how to use it to achieve your financial goals."
              heroImage={Image10}
              imageClass="h-auto md:h-52"
              reversed
            />
          </CardContainer>
        </Stack>
      </div>
    </BoostContainer>
  );
};

export default HowItWorks;
